import React, { useState, useEffect } from 'react';
import { 
  Building, Plus, MapPin, QrCode, Download, Loader2, Trash2, 
  ChevronRight, Eye, Copy, Check, X, Edit, Search, FileText
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

// Checkpoint Card Component
const CheckpointCard = ({ checkpoint, onDelete, onViewQR, deleting }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopyQR = () => {
    navigator.clipboard.writeText(checkpoint.qr_code || checkpoint.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div 
      className="group flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
      data-testid={`checkpoint-${checkpoint.id}`}
    >
      {/* QR Icon */}
      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
        <QrCode className="w-6 h-6 text-white" />
      </div>
      
      {/* Checkpoint Info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-slate-800 truncate">{checkpoint.name}</h4>
        <p className="text-sm text-slate-500 truncate">{checkpoint.location}</p>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className="text-[10px] font-mono bg-slate-50">
            {checkpoint.qr_code || checkpoint.id}
          </Badge>
          <button 
            onClick={handleCopyQR}
            className="text-slate-400 hover:text-blue-600 transition-colors"
            title="Copy QR Code"
          >
            {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
          </button>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onViewQR(checkpoint)}
          className="text-blue-600 hover:bg-blue-50"
        >
          <Eye className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onDelete(checkpoint)}
          disabled={deleting === checkpoint.id}
          className="text-red-600 hover:bg-red-50"
        >
          {deleting === checkpoint.id ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default function AdminSites() {
  const { toast } = useToast();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSite, setShowAddSite] = useState(false);
  const [showSiteDetail, setShowSiteDetail] = useState(null);
  const [showAddCheckpoint, setShowAddCheckpoint] = useState(false);
  const [showQRPreview, setShowQRPreview] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deletingCheckpoint, setDeletingCheckpoint] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [newSite, setNewSite] = useState({ name: '', address: '', description: '' });
  const [newCheckpoint, setNewCheckpoint] = useState({ name: '', location: '', description: '' });

  const loadSites = async () => {
    try {
      const data = await api.getSites();
      setSites(data);
    } catch (error) {
      console.error('Error loading sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCheckpoints = async (siteId) => {
    try {
      const data = await api.getSiteCheckpoints(siteId);
      setCheckpoints(data);
    } catch (error) {
      console.error('Error loading checkpoints:', error);
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    if (showSiteDetail) {
      loadCheckpoints(showSiteDetail.id);
    }
  }, [showSiteDetail]);

  const handleAddSite = async (e) => {
    e.preventDefault();
    if (!newSite.name || !newSite.address) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in site name and address",
      });
      return;
    }

    setSubmitting(true);
    try {
      await api.createSite(newSite);
      setNewSite({ name: '', address: '', description: '' });
      setShowAddSite(false);
      loadSites();
      toast({
        title: "Site Created",
        description: `${newSite.name} has been added successfully`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to create site',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCheckpoint = async (e) => {
    e.preventDefault();
    if (!newCheckpoint.name || !newCheckpoint.location) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in checkpoint name and location",
      });
      return;
    }

    setSubmitting(true);
    try {
      await api.createCheckpoint({
        ...newCheckpoint,
        site_id: showSiteDetail.id,
      });
      setNewCheckpoint({ name: '', location: '', description: '' });
      setShowAddCheckpoint(false);
      loadCheckpoints(showSiteDetail.id);
      toast({
        title: "Checkpoint Created",
        description: `${newCheckpoint.name} has been added with a unique QR code`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to create checkpoint',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCheckpoint = async () => {
    if (!deleteConfirm) return;
    
    setDeletingCheckpoint(deleteConfirm.id);
    try {
      await api.deleteCheckpoint(deleteConfirm.id);
      loadCheckpoints(showSiteDetail.id);
      toast({
        title: "Checkpoint Deleted",
        description: `${deleteConfirm.name} has been removed`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to delete checkpoint',
      });
    } finally {
      setDeletingCheckpoint(null);
      setDeleteConfirm(null);
    }
  };

  const handleDeleteSite = async (siteId) => {
    try {
      await api.deleteSite(siteId);
      setShowSiteDetail(null);
      loadSites();
      toast({
        title: "Site Deleted",
        description: "Site and all checkpoints have been removed",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to delete site',
      });
    }
  };

  const handleDownloadSiteQR = async (siteId, siteName) => {
    try {
      const url = api.getSiteQrPdfUrl(siteId);
      await api.downloadPdf(url, `QR_Codes_${siteName.replace(/\s+/g, '_')}.pdf`);
    } catch (err) {
      toast({ variant: "destructive", title: "Download Failed", description: err.message });
    }
  };

  const handleDownloadAllQR = async () => {
    try {
      const url = api.getAllSitesQrPdfUrl();
      await api.downloadPdf(url, 'All_Sites_QR_Codes.pdf');
    } catch (err) {
      toast({ variant: "destructive", title: "Download Failed", description: err.message });
    }
  };

  // Filter sites by search
  const filteredSites = sites.filter(site => 
    site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    site.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-sites-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Sites & Checkpoints</h1>
            <p className="text-slate-500 text-sm">{sites.length} sites, manage patrol checkpoints</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleDownloadAllQR} variant="outline" className="gap-2">
              <Download className="w-4 h-4" /> All QR Codes
            </Button>
            <Button onClick={() => setShowAddSite(true)} className="bg-blue-700 hover:bg-blue-800 gap-2">
              <Plus className="w-4 h-4" /> Add Site
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search sites..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Sites Grid */}
        {filteredSites.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {searchQuery ? 'No sites found' : 'No sites yet'}
              </h3>
              <p className="text-slate-500 mb-4">
                {searchQuery ? 'Try a different search term' : 'Add your first site to get started'}
              </p>
              {!searchQuery && (
                <Button onClick={() => setShowAddSite(true)} className="bg-blue-700 hover:bg-blue-800">
                  <Plus className="w-4 h-4 mr-2" /> Add First Site
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSites.map((site) => (
              <Card 
                key={site.id} 
                className="cursor-pointer hover:border-blue-300 hover:shadow-lg transition-all group"
                onClick={() => setShowSiteDetail(site)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-sm">
                      <Building className="w-6 h-6 text-white" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-1 text-lg">{site.name}</h3>
                  <p className="text-sm text-slate-500 flex items-center gap-1.5 mb-3">
                    <MapPin className="w-3.5 h-3.5" /> {site.address}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                      <QrCode className="w-3 h-3 mr-1" /> Checkpoints
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Site Modal */}
      <Dialog open={showAddSite} onOpenChange={setShowAddSite}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-slate-900">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-green-700" />
              </div>
              Add New Site
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Create a new patrol site location
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSite} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Site Name *</Label>
              <Input
                placeholder="e.g., Tech Park Tower"
                value={newSite.name}
                onChange={(e) => setNewSite(prev => ({ ...prev, name: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Address *</Label>
              <Input
                placeholder="e.g., 123 Main Street, City"
                value={newSite.address}
                onChange={(e) => setNewSite(prev => ({ ...prev, address: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Description (Optional)</Label>
              <Textarea
                placeholder="Additional details about this site..."
                value={newSite.description}
                onChange={(e) => setNewSite(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddSite(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Site
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Site Detail Modal */}
      <Dialog open={!!showSiteDetail} onOpenChange={(open) => !open && setShowSiteDetail(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-3 text-slate-900">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-sm">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl text-slate-900">{showSiteDetail?.name}</span>
                <p className="text-sm font-normal text-slate-500 flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {showSiteDetail?.address}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-xl">
              <Button 
                onClick={() => handleDownloadSiteQR(showSiteDetail?.id, showSiteDetail?.name)} 
                variant="outline" 
                className="gap-2 flex-1 sm:flex-none"
              >
                <FileText className="w-4 h-4" /> Download All QR (PDF)
              </Button>
              <Button 
                onClick={() => setShowAddCheckpoint(true)} 
                className="bg-blue-700 hover:bg-blue-800 gap-2 flex-1 sm:flex-none"
              >
                <Plus className="w-4 h-4" /> Add Checkpoint
              </Button>
              <Button 
                onClick={() => handleDeleteSite(showSiteDetail?.id)} 
                variant="outline" 
                className="text-red-600 hover:bg-red-50 border-red-200 ml-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete Site
              </Button>
            </div>

            {/* Checkpoints List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-slate-800 text-lg">
                  Checkpoints 
                  <Badge className="ml-2 bg-blue-100 text-blue-700">{checkpoints.length}</Badge>
                </h4>
              </div>
              
              {checkpoints.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <QrCode className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500 font-medium">No checkpoints yet</p>
                  <p className="text-slate-400 text-sm mt-1 mb-4">Add checkpoints for guards to scan during patrols</p>
                  <Button onClick={() => setShowAddCheckpoint(true)} className="bg-blue-700 hover:bg-blue-800">
                    <Plus className="w-4 h-4 mr-2" /> Add First Checkpoint
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {checkpoints.map((cp) => (
                    <CheckpointCard
                      key={cp.id}
                      checkpoint={cp}
                      onDelete={(checkpoint) => setDeleteConfirm(checkpoint)}
                      onViewQR={(checkpoint) => setShowQRPreview(checkpoint)}
                      deleting={deletingCheckpoint}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Checkpoint Modal */}
      <Dialog open={showAddCheckpoint} onOpenChange={setShowAddCheckpoint}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-slate-900">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <QrCode className="w-5 h-5 text-blue-700" />
              </div>
              Add Checkpoint
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Add a new checkpoint to <span className="font-medium">{showSiteDetail?.name}</span>. 
              A unique QR code will be generated automatically.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCheckpoint} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Checkpoint Name *</Label>
              <Input
                placeholder="e.g., Main Entrance, Parking Lot B"
                value={newCheckpoint.name}
                onChange={(e) => setNewCheckpoint(prev => ({ ...prev, name: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Location *</Label>
              <Input
                placeholder="e.g., Ground Floor, North Wing"
                value={newCheckpoint.location}
                onChange={(e) => setNewCheckpoint(prev => ({ ...prev, location: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Instructions (Optional)</Label>
              <Textarea
                placeholder="Special instructions for guards at this checkpoint..."
                value={newCheckpoint.description}
                onChange={(e) => setNewCheckpoint(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            
            {/* QR Code Preview Note */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700 flex items-start gap-2">
                <QrCode className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>A unique QR code will be generated for this checkpoint. You can download it after creation.</span>
              </p>
            </div>
            
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddCheckpoint(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Checkpoint
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Preview Modal */}
      <Dialog open={!!showQRPreview} onOpenChange={(open) => !open && setShowQRPreview(null)}>
        <DialogContent className="bg-white max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Scan QR Code</DialogTitle>
            <DialogDescription className="text-slate-600">
              <span className="font-semibold">{showQRPreview?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {/* Actual QR Code using canvas */}
            <div className="bg-white p-6 rounded-2xl border-2 border-slate-200 shadow-lg inline-block">
              <QRCodeCanvas 
                value={showQRPreview?.qr_code || showQRPreview?.id || ''} 
                size={200}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
            <div className="mt-4 space-y-2">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Location:</span> {showQRPreview?.location}
              </p>
              <p className="text-xs text-slate-400 font-mono bg-slate-100 px-3 py-1.5 rounded-lg inline-block">
                ID: {showQRPreview?.qr_code || showQRPreview?.id}
              </p>
            </div>
          </div>
          <DialogFooter className="justify-center gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowQRPreview(null)}
            >
              Close
            </Button>
            <Button 
              onClick={() => handleDownloadSiteQR(showSiteDetail?.id)}
              className="bg-blue-700 hover:bg-blue-800"
            >
              <Download className="w-4 h-4 mr-2" /> Download All QRs (PDF)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Checkpoint Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Checkpoint?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold">{deleteConfirm?.name}</span>? 
              This action cannot be undone. Any existing scan records will remain but new scans won't work.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteCheckpoint}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingCheckpoint ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
